import React, { useState, useEffect, useCallback } from 'react'
import { Form, Spin, message } from 'antd'
import Handlebars from 'handlebars'
import { HugeRTEWrapper } from '@veritas/huge-rte-wrapper'
import HTMLReactParser from 'html-react-parser';

import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    QuestionCircleOutlined,
    ClearOutlined
} from '@ant-design/icons'

import { StyledButton } from '../../Button';
import { showVariableList } from './ShowVariableList'
import { MODAL_MODES } from '../../../constants'
import { stripHtml } from '../../../utils'

import {
    StyledModal,
    ModalTitle,
} from './Charting.styles'

import {
    ModalHeaderContainer,
    ChartingEntryFormItem,
    Title,
    StyledSelect,
    CompiledChartingEntry,
    ButtonGroup,
    ButtonGroupRight
} from './ChartingEntryModal.styles'

export const ChartingEntryModal = ({
    onCancel,
    editChartingEntry,
    addChartingEntry,
    selectedItem,
    modalMode,
    loading,
    templates = [],
    prescriptions = {}
}) => {
    const [form] = Form.useForm()
    const [rawEntry, setRawEntry] = useState('')
    const [compiledEntry, setCompiledEntry] = useState('')
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [selectedPrescription, setSelectedPrescription] = useState(null)

    const queuedPrescriptionOptions = Object.values(prescriptions).map(prescription => ({
        value: prescription._id,
        label: prescription.name,
        key: prescription._id
    }))

    const templateOptions = templates.map(template => ({
        value: template.message,
        label: template.title,
        key: template._id
    }))

    const handleSubmit = async () => {
        if (stripHtml(compiledEntry).length < 3) {
            message.warning('Charting note must be at least 3 characters.')
            return
        }

        const submitFn = !!selectedItem
            ? editChartingEntry
            : addChartingEntry

        const payload = { note: compiledEntry }

        if (!!selectedItem) {
            payload._id = selectedItem._id
        }

        const success = await submitFn(payload)
        if (success) {
            handleCancel()
        }
    }

    const handleCancel = () => {
        setRawEntry('')
        setCompiledEntry('')
        setSelectedTemplate(null)
        setSelectedPrescription(null)
        if (modalMode) {
            form.resetFields()
        }
        onCancel()
    }

    const handleTextChange = (value) => {
        setRawEntry(value)
        compileTemplate(value)
    }

    const handleTemplateSelect = (templateMessage) => {
        setSelectedTemplate(templateMessage)
        setRawEntry(templateMessage)
        compileTemplate(templateMessage)
    }

    const compileTemplate = useCallback((template, prescriptionId = selectedPrescription) => {
        try {
            if (!template) {
                setCompiledEntry('')
                return
            }

            const prescription = prescriptions[prescriptionId] || {}
            const compiled = Handlebars.compile(template)
            const result = compiled(prescription)
            setCompiledEntry(result)
        } catch (error) {
            console.error('Template compilation error:', error)
            setCompiledEntry(template) 
        }
    }, [prescriptions, selectedPrescription])

    const handlePrescriptionSelect = useCallback((prescriptionId) => {
        setSelectedPrescription(prescriptionId)
        compileTemplate(rawEntry, prescriptionId)
    }, [rawEntry, compileTemplate])

    const handleClear = () => {
        setRawEntry('')
        setCompiledEntry('')
        setSelectedTemplate(null)
        setSelectedPrescription(null)
        if (modalMode) {
            form.resetFields()
        }
    }

    useEffect(() => {
        if (selectedItem) {
            setRawEntry(selectedItem.note || '')
            setCompiledEntry(selectedItem.note || '')
        } else {
            handleClear()
        }
    }, [selectedItem])

    const ModalHeader = () => {
        if (!modalMode) return null

        return (
            <ModalHeaderContainer>
                <ModalTitle>
                    {modalMode === MODAL_MODES.ADD
                        ? 'Add Charting Entry'
                        : 'Edit Charting Entry'
                    }
                </ModalTitle>
                <StyledButton
                    id="charting-entry-clear-btn"
                    type="button"
                    size="medium"
                    Icon={ClearOutlined}
                    tooltip="Clear Note"
                    onClick={handleClear}
                />
            </ModalHeaderContainer>
        )
    }

    return (
        <StyledModal
            centered
            width={800}
            open={!!modalMode}
            title={<ModalHeader />}
            closable={false}
            footer={null}
        >
            <Spin spinning={loading}>
                <Form
                    form={form}
                    onFinish={handleSubmit}
                >
                    <ChartingEntryFormItem>
                        <Title>Selected Prescription</Title>
                        <StyledSelect
                            id="charting-entry-prescription-select"
                            placeholder="Select a prescription to templatize"
                            options={queuedPrescriptionOptions}
                            value={selectedPrescription}
                            onSelect={handlePrescriptionSelect}
                        />
                    </ChartingEntryFormItem>

                    <ChartingEntryFormItem>
                        <Title>Template Name</Title>
                        <StyledSelect
                            id="charting-entry-template-select"
                            placeholder="Select a template"
                            options={templateOptions}
                            value={selectedTemplate}
                            onSelect={handleTemplateSelect}
                        />
                    </ChartingEntryFormItem>

                    <ChartingEntryFormItem>
                        <Title>Template</Title>
                        <HugeRTEWrapper
                            value={rawEntry}
                            onChange={handleTextChange}
                            autoSize={{ minRows: 3 }}
                            style={{ minHeight: '100px' }}
                        />
                    </ChartingEntryFormItem>

                    <ChartingEntryFormItem>
                        <Title>Rendered message</Title>
                        <CompiledChartingEntry>
                            {HTMLReactParser(compiledEntry)}
                        </CompiledChartingEntry>
                    </ChartingEntryFormItem>

                    <ButtonGroup>
                        <StyledButton
                            id="charting-entry-variables-btn"
                            type="button"
                            size="medium"
                            Icon={QuestionCircleOutlined}
                            tooltip="Template Variables"
                            onClick={showVariableList}
                        />
                        <ButtonGroupRight>
                            <StyledButton
                                id="charting-entry-cancel-btn"
                                type="button"
                                size="medium"
                                Icon={CloseCircleOutlined}
                                tooltip="Cancel"
                                color="red"
                                onClick={handleCancel}
                            />
                            <StyledButton
                                id="charting-entry-submit-btn"
                                type="submit"
                                size="medium"
                                Icon={CheckCircleOutlined}
                                tooltip="Submit"
                            />
                        </ButtonGroupRight>
                    </ButtonGroup>
                </Form>
            </Spin>
        </StyledModal>
    )
}
